import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Phone, 
  MapPin, 
  Navigation, 
  Camera, 
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Smartphone,
  Battery,
  Monitor,
  Wifi,
  Settings,
  Package,
  Send,
  X,
  Check,
  Trash2,
  RotateCcw,
  Play,
  MapPinned,
  XCircle,
  ScanLine,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Status Flow Component
const StatusFlowBar = ({ currentStatus, onStatusUpdate, loading }) => {
  const statuses = [
    { id: 'PICKUP_SCHEDULED', label: 'Scheduled', icon: '📅' },
    { id: 'EN_ROUTE', label: 'En Route', icon: '🚗' },
    { id: 'ARRIVED', label: 'Arrived', icon: '📍' },
    { id: 'PQ_IN_PROGRESS', label: 'QC Started', icon: '📋' },
    { id: 'PQ_COMPLETED', label: 'Completed', icon: '✅' }
  ];

  const getCurrentIndex = () => {
    const idx = statuses.findIndex(s => s.id === currentStatus);
    return idx >= 0 ? idx : 0;
  };

  const currentIndex = getCurrentIndex();
  const nextStatus = statuses[currentIndex + 1];

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
      {/* Progress Bar */}
      <div className="flex items-center justify-between mb-3">
        {statuses.map((status, idx) => (
          <div key={status.id} className="flex items-center">
            <div className={`flex flex-col items-center ${idx <= currentIndex ? 'opacity-100' : 'opacity-40'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                idx < currentIndex ? 'bg-green-500 text-white' :
                idx === currentIndex ? 'bg-[#2b3380] text-white' :
                'bg-gray-200 text-gray-500'
              }`}>
                {idx < currentIndex ? '✓' : status.icon}
              </div>
              <span className="text-xs mt-1 text-center max-w-[60px]">{status.label}</span>
            </div>
            {idx < statuses.length - 1 && (
              <div className={`w-6 h-0.5 mx-1 ${idx < currentIndex ? 'bg-green-500' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Next Action Button */}
      {nextStatus && currentStatus !== 'PQ_COMPLETED' && currentStatus !== 'PICKUP_FAILED' && (
        <button
          onClick={() => onStatusUpdate(nextStatus.id)}
          disabled={loading}
          className="w-full py-3 bg-gradient-to-r from-[#2b3380] to-[#4a5cc5] text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid={`status-update-${nextStatus.id.toLowerCase()}`}
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              {nextStatus.id === 'EN_ROUTE' && <Play className="w-5 h-5" />}
              {nextStatus.id === 'ARRIVED' && <MapPinned className="w-5 h-5" />}
              {nextStatus.id === 'PQ_IN_PROGRESS' && <ScanLine className="w-5 h-5" />}
              Mark as {nextStatus.label}
            </>
          )}
        </button>
      )}
    </div>
  );
};

// Failure Modal Component
const FailureModal = ({ isOpen, onClose, onSubmit, loading }) => {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  
  const failureReasons = [
    'Customer Not Available',
    'Wrong Address',
    'Customer Changed Mind',
    'Device Mismatch',
    'Price Not Agreed',
    'Customer Rescheduled',
    'Other'
  ];

  if (!isOpen) return null;

  const handleSubmit = () => {
    const finalReason = reason === 'Other' ? customReason : reason;
    if (!finalReason) {
      alert('Please select or enter a reason');
      return;
    }
    onSubmit(finalReason);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
      <div className="bg-white w-full max-w-md rounded-t-2xl p-6 animate-slide-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">Mark Pickup as Failed</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-sm text-gray-500 mb-4">Select the reason for pickup failure:</p>
        
        <div className="space-y-2 mb-4">
          {failureReasons.map((r) => (
            <button
              key={r}
              onClick={() => setReason(r)}
              className={`w-full p-3 rounded-lg border text-left transition-all ${
                reason === r 
                  ? 'border-red-500 bg-red-50 text-red-700' 
                  : 'border-gray-200 hover:border-red-300'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
        
        {reason === 'Other' && (
          <textarea
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            placeholder="Enter reason..."
            className="w-full p-3 border border-gray-200 rounded-lg mb-4"
            rows={2}
          />
        )}
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-gray-300 rounded-xl font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || (!reason || (reason === 'Other' && !customReason))}
            className="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <XCircle className="w-5 h-5" />}
            Confirm Failed
          </button>
        </div>
      </div>
    </div>
  );
};

// IMEI Scanner Component
const IMEICapture = ({ initialImei, onSave, leadId }) => {
  const [imei, setImei] = useState(initialImei || '');
  const [imei2, setImei2] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(!!initialImei);
  const [error, setError] = useState('');

  const validateIMEI = (value) => {
    if (!value) return true;
    return /^\d{15}$/.test(value);
  };

  const handleSave = async () => {
    setError('');
    
    if (!imei) {
      setError('IMEI is required');
      return;
    }
    
    if (!validateIMEI(imei)) {
      setError('IMEI must be exactly 15 digits');
      return;
    }
    
    if (imei2 && !validateIMEI(imei2)) {
      setError('IMEI2 must be exactly 15 digits');
      return;
    }

    setSaving(true);
    try {
      const engineer = JSON.parse(localStorage.getItem('engineer') || '{}');
      const response = await fetch(`${API}/api/buyback/leads/${leadId}/update-imei`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imei,
          imei2: imei2 || null,
          serial_number: serialNumber || null,
          engineer_id: engineer.employee_id || 'ENG-001'
        })
      });

      if (response.ok) {
        setSaved(true);
        onSave?.({ imei, imei2, serialNumber });
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save IMEI');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <ScanLine className="w-5 h-5 text-[#2b3380]" />
          Device IMEI
        </h3>
        {saved && (
          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
            <CheckCircle className="w-3 h-3" /> Saved
          </span>
        )}
      </div>
      
      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}
      
      <div className="space-y-3">
        <div>
          <label className="block text-sm text-gray-600 mb-1">IMEI 1 *</label>
          <input
            type="tel"
            value={imei}
            onChange={(e) => {
              setImei(e.target.value.replace(/\D/g, '').slice(0, 15));
              setSaved(false);
            }}
            placeholder="Enter 15-digit IMEI"
            className={`w-full px-4 py-3 border rounded-lg text-lg tracking-wider ${
              imei && !validateIMEI(imei) ? 'border-red-300' : 'border-gray-200'
            }`}
            maxLength={15}
            data-testid="imei-input"
          />
          <p className="text-xs text-gray-400 mt-1">Dial *#06# to view IMEI</p>
        </div>
        
        <div>
          <label className="block text-sm text-gray-600 mb-1">IMEI 2 (Dual SIM)</label>
          <input
            type="tel"
            value={imei2}
            onChange={(e) => {
              setImei2(e.target.value.replace(/\D/g, '').slice(0, 15));
              setSaved(false);
            }}
            placeholder="Enter 15-digit IMEI (optional)"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
            maxLength={15}
            data-testid="imei2-input"
          />
        </div>
        
        <div>
          <label className="block text-sm text-gray-600 mb-1">Serial Number</label>
          <input
            type="text"
            value={serialNumber}
            onChange={(e) => {
              setSerialNumber(e.target.value);
              setSaved(false);
            }}
            placeholder="Enter serial number (optional)"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
            data-testid="serial-input"
          />
        </div>
        
        {!saved && (
          <button
            onClick={handleSave}
            disabled={saving || !imei}
            className="w-full py-3 bg-[#2b3380] text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="save-imei-btn"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5" />
                Save IMEI
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// Signature Pad Component
const SignaturePad = ({ onSave, onClear }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onClear?.();
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave?.(dataUrl);
  };

  return (
    <div className="space-y-3">
      <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={400}
          height={200}
          className="w-full touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>
      <div className="flex gap-2">
        <button
          onClick={clearSignature}
          className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-600 flex items-center justify-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          Clear
        </button>
        <button
          onClick={saveSignature}
          disabled={!hasSignature}
          className="flex-1 py-2 bg-[#2b3380] text-white rounded-lg flex items-center justify-center gap-2 disabled:bg-gray-300"
        >
          <Check className="w-4 h-4" />
          Confirm
        </button>
      </div>
    </div>
  );
};

// Camera Component
const CameraCapture = ({ onCapture, photos, onRemove }) => {
  const fileInputRef = useRef(null);

  const handleCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onCapture(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-3">
      {/* Photo Grid */}
      <div className="grid grid-cols-3 gap-2">
        {photos.map((photo, index) => (
          <div key={index} className="relative aspect-square rounded-lg overflow-hidden">
            <img src={photo} alt={`Device ${index + 1}`} className="w-full h-full object-cover" />
            <button
              onClick={() => onRemove(index)}
              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        
        {/* Add Photo Button */}
        {photos.length < 6 && (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-[#2b3380] hover:text-[#2b3380] transition-colors"
          >
            <Camera className="w-6 h-6" />
            <span className="text-xs mt-1">Add Photo</span>
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCapture}
        className="hidden"
      />

      <p className="text-xs text-gray-500 text-center">
        {photos.length}/6 photos • Tap to capture device images
      </p>
    </div>
  );
};

// QC Question Component - Open by default, collapse after selection
const QCQuestion = ({ question, value, onChange, autoFocus }) => {
  // Open by default if no value selected, collapse after selection
  const [expanded, setExpanded] = useState(!value);

  const handleSelect = (option) => {
    onChange(option);
    // Auto-collapse after selection
    setTimeout(() => setExpanded(false), 150);
  };

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${
      value ? 'border-green-300 bg-green-50/30' : 'border-gray-200'
    }`}>
      {/* Header - Always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
          value ? 'bg-green-50' : 'bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-2">
          {value ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
          )}
          <span className={`font-medium ${value ? 'text-green-800' : 'text-gray-800'}`}>
            {question.name}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Show selected result prominently */}
          {value && (
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-600 text-white">
              {value.text}
            </span>
          )}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>
      
      {/* Options - Expanded view */}
      {expanded && (
        <div className="p-3 bg-white border-t border-gray-100">
          <div className="grid grid-cols-1 gap-2">
            {question.options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleSelect(option)}
                className={`w-full p-3 rounded-lg border text-left transition-all ${
                  value?.id === option.id
                    ? 'bg-[#2b3380] text-white border-[#2b3380] shadow-md'
                    : 'border-gray-200 hover:border-orange-400 hover:bg-orange-50 active:bg-orange-100 active:scale-[0.98]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{option.text}</span>
                  {value?.id === option.id && <CheckCircle className="w-5 h-5" />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Pickup Detail Component
const PickupDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [pickup, setPickup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details'); // details, imei, qc, photos, signature
  const [qcResponses, setQcResponses] = useState({});
  const [photos, setPhotos] = useState([]);
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [qcConfig, setQcConfig] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  
  // New state for status flow
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [imeiSaved, setImeiSaved] = useState(false);

  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    if (isDemo) {
      // Demo mode - just update locally
      setPickup(prev => ({ ...prev, status: newStatus }));
      if (newStatus === 'PQ_IN_PROGRESS') {
        setActiveTab('qc');
      }
      return;
    }

    setStatusUpdating(true);
    try {
      const engineer = JSON.parse(localStorage.getItem('engineer') || '{}');
      
      // Get current location if available
      let latitude = null;
      let longitude = null;
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          latitude = position.coords.latitude;
          longitude = position.coords.longitude;
        } catch (e) {
          console.log('Could not get location:', e);
        }
      }

      const response = await fetch(`${API}/api/buyback/leads/${leadId}/update-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus,
          engineer_id: engineer.employee_id || 'ENG-001',
          engineer_name: engineer.name,
          latitude,
          longitude
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPickup(data.lead);
        
        // Auto-navigate to QC tab when starting PQ
        if (newStatus === 'PQ_IN_PROGRESS') {
          setActiveTab('qc');
        }
      } else {
        const error = await response.json();
        alert(`Failed to update status: ${error.detail || 'Please try again'}`);
      }
    } catch (err) {
      console.error('Status update error:', err);
      alert('Network error. Please try again.');
    } finally {
      setStatusUpdating(false);
    }
  };

  // Handle pickup failure
  const handlePickupFailed = async (reason) => {
    if (isDemo) {
      setPickup(prev => ({ ...prev, status: 'PICKUP_FAILED' }));
      setShowFailureModal(false);
      alert('Demo: Pickup marked as failed');
      setTimeout(() => navigate('/engineer'), 1500);
      return;
    }

    setStatusUpdating(true);
    try {
      const engineer = JSON.parse(localStorage.getItem('engineer') || '{}');
      
      const response = await fetch(`${API}/api/buyback/leads/${leadId}/update-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'PICKUP_FAILED',
          engineer_id: engineer.employee_id || 'ENG-001',
          engineer_name: engineer.name,
          failure_reason: reason
        })
      });

      if (response.ok) {
        setShowFailureModal(false);
        alert('Pickup marked as failed');
        setTimeout(() => navigate('/engineer'), 1500);
      } else {
        const error = await response.json();
        alert(`Failed: ${error.detail || 'Please try again'}`);
      }
    } catch (err) {
      alert('Network error. Please try again.');
    } finally {
      setStatusUpdating(false);
    }
  };

  // Fetch pickup and QC config
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch pickup lead details
        const pickupRes = await fetch(`${API}/api/buyback/leads/${leadId}`);
        if (pickupRes.ok) {
          const data = await pickupRes.json();
          if (data.lead) {
            setPickup(data.lead);
            setIsDemo(false);
            
            // If PQ already completed, load existing responses
            if (data.lead.pq_responses) {
              setQcResponses(data.lead.pq_responses);
            }
          }
        } else if (leadId.includes('DEMO')) {
          // Demo lead
          setIsDemo(true);
          setPickup({
            lead_id: leadId,
            status: 'PICKUP_SCHEDULED',
            customer_info: {
              name: 'Demo Customer',
              mobile: '9876543210',
              address: '123 Demo Address, Koramangala',
              city: 'Bangalore',
              pincode: '560034'
            },
            device_info: {
              brand: 'Apple',
              model: 'iPhone 12',
              variant: '128GB',
              storage: '128GB'
            },
            quoted_price: 17610,
            pickup_scheduled_date: new Date().toISOString().split('T')[0],
            pickup_scheduled_time: '10:00 AM - 12:00 PM'
          });
        }

        // Fetch QC config from new Android template endpoint
        const qcRes = await fetch(`${API}/api/buyback/qc-template/android`);
        if (qcRes.ok) {
          const qcData = await qcRes.json();
          if (qcData.success && qcData.template && qcData.template.categories) {
            setQcConfig(qcData.template);
            console.log(`Loaded QC template with ${qcData.total_parameters} parameters`);
          } else {
            // Fallback QC config
            loadDemoQCConfig();
          }
        } else {
          loadDemoQCConfig();
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        // Demo data fallback
        setIsDemo(true);
        setPickup({
          lead_id: leadId,
          status: 'PICKUP_SCHEDULED',
          customer_info: {
            name: 'Demo Customer',
            mobile: '9876543210',
            address: '123 Demo Address',
            city: 'Bangalore',
            pincode: '560034'
          },
          device_info: {
            brand: 'Apple',
            model: 'iPhone 12',
            variant: '128GB',
            storage: '128GB'
          },
          quoted_price: 17610,
          pickup_scheduled_date: new Date().toISOString().split('T')[0],
          pickup_scheduled_time: '10:00 AM - 12:00 PM'
        });
        
        loadDemoQCConfig();
      } finally {
        setLoading(false);
      }
    };

    const loadDemoQCConfig = () => {
      // Comprehensive 53-parameter Android QC config (fallback)
      setQcConfig({
        categories: [
          {
            id: 'device_status',
            name: 'Device Status',
            order: 1,
            parameters: [
              {
                id: 'power_status',
                name: 'Power Status',
                options: [
                  { id: 'ps_1', text: 'Working / Power ON', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'ps_2', text: 'Dead Phone (DOA)', severity: 'Salvage', weight: 100, multiplier: 1 }
                ]
              },
              {
                id: 'phone_lock',
                name: 'Phone Lock Status',
                options: [
                  { id: 'pl_1', text: 'No Lock / Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'pl_2', text: 'Finance / Account Locked', severity: 'Salvage', weight: 100, multiplier: 1 }
                ]
              },
              {
                id: 'imei_status',
                name: 'IMEI Status',
                options: [
                  { id: 'imei_1', text: 'Purchased in India', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'imei_2', text: 'Not Purchased in India', severity: 'Good', weight: 0, multiplier: 0 }
                ]
              }
            ]
          },
          {
            id: 'battery',
            name: 'Battery',
            order: 2,
            parameters: [
              {
                id: 'battery_condition',
                name: 'Battery Condition',
                options: [
                  { id: 'bat_1', text: 'Good Health', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'bat_2', text: 'Fake Charge', severity: 'Medium', weight: 8, multiplier: 1 },
                  { id: 'bat_3', text: 'Service / Weak', severity: 'Medium', weight: 8, multiplier: 1 },
                  { id: 'bat_4', text: 'Swollen / Faulty', severity: 'High', weight: 8, multiplier: 1 }
                ]
              }
            ]
          },
          {
            id: 'display',
            name: 'Display',
            order: 3,
            parameters: [
              {
                id: 'display_status',
                name: 'Display Status',
                options: [
                  { id: 'dsp_1', text: 'Good Display', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'dsp_2', text: 'Glass Broken', severity: 'High', weight: 7, multiplier: 1 },
                  { id: 'dsp_3', text: 'Dead / Blank / Lines', severity: 'High', weight: 35, multiplier: 1 },
                  { id: 'dsp_4', text: 'Light Fading', severity: 'Low', weight: 35, multiplier: 0.25 },
                  { id: 'dsp_5', text: 'Heavy Fading', severity: 'Medium', weight: 35, multiplier: 0.5 },
                  { id: 'dsp_6', text: 'Very Heavy Fading', severity: 'High', weight: 35, multiplier: 0.75 }
                ]
              }
            ]
          },
          {
            id: 'touch',
            name: 'Touch',
            order: 4,
            parameters: [
              {
                id: 'touch_panel',
                name: 'Touch Panel',
                options: [
                  { id: 'tch_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'tch_2', text: 'Auto Touch', severity: 'High', weight: 7, multiplier: 1 },
                  { id: 'tch_3', text: 'Not Working', severity: 'High', weight: 35, multiplier: 1 }
                ]
              }
            ]
          },
          {
            id: 'network',
            name: 'Network & Connectivity',
            order: 5,
            parameters: [
              {
                id: 'cellular_network',
                name: 'Cellular Network',
                options: [
                  { id: 'net_1', text: 'Good Signal', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'net_2', text: 'Low Signal', severity: 'Medium', weight: 10, multiplier: 1 },
                  { id: 'net_3', text: 'No Signal', severity: 'High', weight: 10, multiplier: 1 }
                ]
              },
              {
                id: 'wifi',
                name: 'WiFi',
                options: [
                  { id: 'wifi_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'wifi_2', text: 'Not Working', severity: 'High', weight: 7, multiplier: 1 }
                ]
              },
              {
                id: 'bluetooth',
                name: 'Bluetooth',
                options: [
                  { id: 'bt_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'bt_2', text: 'Not Working', severity: 'High', weight: 5, multiplier: 1 }
                ]
              }
            ]
          },
          {
            id: 'camera',
            name: 'Camera',
            order: 6,
            parameters: [
              {
                id: 'rear_camera',
                name: 'Rear Camera',
                options: [
                  { id: 'rcam_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'rcam_2', text: 'Blur / Spots', severity: 'Medium', weight: 8, multiplier: 0.5 },
                  { id: 'rcam_3', text: 'Not Working', severity: 'Medium', weight: 8, multiplier: 1 }
                ]
              },
              {
                id: 'front_camera',
                name: 'Front Camera',
                options: [
                  { id: 'fcam_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'fcam_2', text: 'Not Working', severity: 'Medium', weight: 7, multiplier: 1 }
                ]
              }
            ]
          },
          {
            id: 'functional',
            name: 'Functional',
            order: 7,
            parameters: [
              {
                id: 'charging_slot',
                name: 'Charging Slot',
                options: [
                  { id: 'chg_1', text: 'Charging OK', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'chg_2', text: 'Not Charging', severity: 'High', weight: 2, multiplier: 1 }
                ]
              },
              {
                id: 'speaker',
                name: 'Speaker',
                options: [
                  { id: 'spk_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'spk_2', text: 'Not Working', severity: 'Low', weight: 0.5, multiplier: 1 }
                ]
              },
              {
                id: 'mic',
                name: 'Mic',
                options: [
                  { id: 'mic_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'mic_2', text: 'Not Working', severity: 'Low', weight: 0.5, multiplier: 1 }
                ]
              },
              {
                id: 'ear_receiver',
                name: 'Ear Receiver',
                options: [
                  { id: 'ear_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'ear_2', text: 'Not Working', severity: 'Low', weight: 0.5, multiplier: 1 }
                ]
              },
              {
                id: 'vibrator',
                name: 'Vibrator',
                options: [
                  { id: 'vib_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'vib_2', text: 'Not Working', severity: 'Low', weight: 0.5, multiplier: 1 }
                ]
              },
              {
                id: 'proximity_sensor',
                name: 'Proximity Sensor',
                options: [
                  { id: 'prox_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'prox_2', text: 'Not Working', severity: 'Medium', weight: 1, multiplier: 1 }
                ]
              },
              {
                id: 'volume_power_keys',
                name: 'Volume / Power Keys',
                options: [
                  { id: 'keys_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'keys_2', text: 'Not Working', severity: 'Low', weight: 0.5, multiplier: 1 }
                ]
              },
              {
                id: 'flash_light',
                name: 'Flash Light',
                options: [
                  { id: 'flash_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'flash_2', text: 'Not Working', severity: 'Low', weight: 0.5, multiplier: 1 }
                ]
              },
              {
                id: 'motion_sensor',
                name: 'Motion Sensor',
                options: [
                  { id: 'motion_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'motion_2', text: 'Not Working', severity: 'High', weight: 5, multiplier: 1 }
                ]
              },
              {
                id: 'audio_recording',
                name: 'Audio Recording',
                options: [
                  { id: 'audio_1', text: 'Working', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'audio_2', text: 'Not Working', severity: 'Medium', weight: 3, multiplier: 1 }
                ]
              }
            ]
          },
          {
            id: 'physical',
            name: 'Physical / Cosmetic',
            order: 8,
            parameters: [
              {
                id: 'body_condition',
                name: 'Body Condition',
                options: [
                  { id: 'body_1', text: 'Good Condition', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'body_2', text: 'Bent Phone', severity: 'High', weight: 10, multiplier: 1 },
                  { id: 'body_3', text: 'Loose Screen / Pasting', severity: 'Low', weight: 3, multiplier: 1 }
                ]
              },
              {
                id: 'back_panel',
                name: 'Back Panel',
                options: [
                  { id: 'back_1', text: 'Good Condition', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'back_2', text: 'Broken / Missing', severity: 'Low', weight: 5, multiplier: 1 }
                ]
              },
              {
                id: 'frame_condition',
                name: 'Frame Condition',
                options: [
                  { id: 'frame_1', text: 'Good Condition', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'frame_2', text: 'Heavy Used', severity: 'Low', weight: 3, multiplier: 0.5 }
                ]
              },
              {
                id: 'camera_lens',
                name: 'Camera Lens',
                options: [
                  { id: 'lens_1', text: 'Good Condition', severity: 'Good', weight: 0, multiplier: 0 },
                  { id: 'lens_2', text: 'Broken', severity: 'Low', weight: 0.5, multiplier: 1 }
                ]
              }
            ]
          },
          {
            id: 'packaging',
            name: 'Packaging & Accessories',
            order: 9,
            parameters: [
              {
                id: 'box_accessories',
                name: 'Box & Accessories',
                is_topup: true,
                options: [
                  { id: 'pkg_1', text: 'Only Device', severity: 'Good', topup: 0 },
                  { id: 'pkg_2', text: 'Device + Box', severity: 'Good', topup: 100 },
                  { id: 'pkg_3', text: 'Device + Box + Charger', severity: 'Good', topup: 250 },
                  { id: 'pkg_4', text: 'Full Kit', severity: 'Good', topup: 300 }
                ]
              }
            ]
          }
        ]
      });
    };

    fetchData();
  }, [leadId]);

  // Display type is determined automatically (default OLED)
  const displayType = 'OLED';
  
  // State for grade calculation (used for price negotiation display)
  const [gradePreview, setGradePreview] = useState(null);

  const handleQcChange = (parameterId, option) => {
    setQcResponses(prev => ({
      ...prev,
      [parameterId]: option
    }));
  };
  
  // Calculate grade preview when responses change (for price negotiation)
  useEffect(() => {
    const calculateGradePreview = async () => {
      if (Object.keys(qcResponses).length < 3) {
        setGradePreview(null);
        return;
      }
      
      // Build response map
      const responseMap = {};
      Object.entries(qcResponses).forEach(([paramId, option]) => {
        responseMap[paramId] = option.id || option;
      });
      
      try {
        const response = await fetch(`${API}/api/buyback/calculate-grade`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            responses: responseMap,
            mbp: pickup?.quoted_price || 0,
            display_type: displayType
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setGradePreview(result);
          }
        }
      } catch (err) {
        console.log('Grade preview error:', err);
      }
    };
    
    // Debounce the API call
    const timer = setTimeout(calculateGradePreview, 500);
    return () => clearTimeout(timer);
  }, [qcResponses, displayType, pickup?.quoted_price]);

  const handlePhotoCapture = (photo) => {
    setPhotos(prev => [...prev, photo]);
  };

  const handlePhotoRemove = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitPQ = async () => {
    if (Object.keys(qcResponses).length === 0) {
      alert('Please complete at least some QC checks');
      return;
    }
    if (photos.length === 0) {
      alert('Please capture at least one device photo');
      return;
    }
    if (!signature) {
      alert('Please get customer signature');
      return;
    }

    setSubmitting(true);
    try {
      const engineer = JSON.parse(localStorage.getItem('engineer') || '{}');
      
      // Build response map - use option IDs for backend compatibility
      const responseMap = {};
      Object.entries(qcResponses).forEach(([paramId, option]) => {
        responseMap[paramId] = option.id || option;
      });

      // For demo leads, just show success
      if (isDemo || leadId.includes('DEMO')) {
        setSubmitSuccess(true);
        setCalculatedPrice({
          quoted: pickup.quoted_price,
          final: Math.round(pickup.quoted_price * 0.95), // Demo: 95% of quoted
          grade: 'Z2',
          grade_name: 'SUPERB',
          difference: Math.round(pickup.quoted_price * 0.05)
        });
        alert('Demo: PQ submitted successfully!');
        setTimeout(() => navigate('/engineer'), 2000);
        return;
      }

      // Submit PQ to backend
      const response = await fetch(`${API}/api/buyback/leads/${leadId}/submit-pq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pq_responses: responseMap,
          engineer_id: engineer.employee_id || 'ENG-001',
          photos: photos.length, // Don't send actual photos in this request, just count
          signature: true, // Just flag that signature was captured
          notes: `PQ completed by ${engineer.name || 'Engineer'}`,
          display_type: displayType
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Use the pricing info from submit-pq response
        setCalculatedPrice({
          quoted: pickup.quoted_price,
          final: result.pricing?.final_price || pickup.quoted_price,
          grade: result.grade,
          grade_name: result.grade_name,
          selling_grade: result.selling_grade,
          warranty_tier: result.warranty_tier,
          difference: result.pricing?.total_deduction || 0,
          topup: result.pricing?.packaging_topup || 0,
          parts_needed: result.parts_needed || [],
          issues_count: result.issues_count || 0
        });
        
        setSubmitSuccess(true);
        alert(`PQ submitted successfully!\nGrade: ${result.grade} (${result.grade_name})\nFinal Price: ₹${result.pricing?.final_price?.toLocaleString() || 'N/A'}`);
        setTimeout(() => navigate('/engineer'), 2000);
      } else {
        const error = await response.json();
        alert(`Submission failed: ${error.detail || 'Please try again.'}`);
      }
    } catch (err) {
      console.error('Submit error:', err);
      // Demo mode fallback
      setSubmitSuccess(true);
      setCalculatedPrice({
        quoted: pickup.quoted_price,
        final: Math.round(pickup.quoted_price * 0.95),
        grade: 'Z2',
        grade_name: 'SUPERB',
        difference: Math.round(pickup.quoted_price * 0.05)
      });
      alert('PQ submitted successfully! (Demo mode)');
      setTimeout(() => navigate('/engineer'), 2000);
    } finally {
      setSubmitting(false);
    }
  };

  const openMaps = () => {
    const address = `${pickup.customer_info.address}, ${pickup.customer_info.city} ${pickup.customer_info.pincode}`;
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(url, '_blank');
  };

  // Start turn-by-turn navigation
  const startNavigation = () => {
    const address = `${pickup.customer_info?.address || ''}, ${pickup.customer_info?.city || ''} ${pickup.customer_info?.pincode || ''}`;
    // Use directions mode for turn-by-turn navigation
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`;
    window.open(url, '_blank');
  };

  const callCustomer = () => {
    window.location.href = `tel:${pickup.customer_info.mobile}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2b3380]"></div>
      </div>
    );
  }

  if (!pickup) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
        <AlertCircle className="w-16 h-16 text-gray-300 mb-4" />
        <p className="text-gray-500">Pickup not found</p>
        <button
          onClick={() => navigate('/engineer')}
          className="mt-4 px-6 py-2 bg-[#2b3380] text-white rounded-lg"
        >
          Go Back
        </button>
      </div>
    );
  }

  const qcProgress = qcConfig 
    ? Math.round((Object.keys(qcResponses).length / qcConfig.categories.reduce((sum, c) => sum + c.parameters.length, 0)) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-[#2b3380] text-white px-4 pt-12 pb-6">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate('/engineer')} className="p-2 -ml-2">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="font-bold">{pickup.lead_id}</h1>
            <p className="text-sm text-blue-200">{pickup.status?.replace(/_/g, ' ')}</p>
          </div>
        </div>

        {/* Device Card */}
        <div className="bg-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Smartphone className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold">{pickup.device_info.brand} {pickup.device_info.model}</h2>
              <p className="text-sm text-blue-200">{pickup.device_info.variant || pickup.device_info.storage}</p>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold">₹{pickup.quoted_price?.toLocaleString()}</p>
              <p className="text-xs text-blue-200">Quoted</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="px-4 -mt-3">
        <div className="bg-white rounded-xl shadow-sm p-1 flex">
          {[
            { id: 'details', label: 'Details' },
            { id: 'imei', label: imeiSaved || pickup?.device_info?.imei ? '✓ IMEI' : 'IMEI' },
            { id: 'qc', label: `QC (${qcProgress}%)` },
            { id: 'photos', label: `Photos (${photos.length})` },
            { id: 'signature', label: signature ? '✓ Sign' : 'Sign' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'bg-[#2b3380] text-white' 
                  : 'text-gray-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-4 py-4">
        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="space-y-4">
            {/* Status Flow Bar */}
            <StatusFlowBar 
              currentStatus={pickup.status} 
              onStatusUpdate={handleStatusUpdate}
              loading={statusUpdating}
            />
            
            {/* Customer Info */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3">Customer Details</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="font-semibold text-gray-600">
                      {pickup.customer_info.name?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{pickup.customer_info.name}</p>
                    <p className="text-sm text-gray-500">+91 {pickup.customer_info.mobile}</p>
                  </div>
                  <button
                    onClick={callCustomer}
                    className="ml-auto p-3 bg-green-100 text-green-600 rounded-full"
                  >
                    <Phone className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex items-start gap-3 pt-3 border-t border-gray-100">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-gray-800">{pickup.customer_info.address}</p>
                    <p className="text-sm text-gray-500">{pickup.customer_info.city} - {pickup.customer_info.pincode}</p>
                  </div>
                  <button
                    onClick={openMaps}
                    className="p-3 bg-blue-100 text-blue-600 rounded-full"
                    title="View on Map"
                  >
                    <MapPin className="w-5 h-5" />
                  </button>
                </div>

                {/* Start Navigation Button */}
                <button
                  onClick={startNavigation}
                  className="w-full mt-4 flex items-center justify-center gap-2 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 active:scale-[0.98] transition-all"
                  data-testid="start-navigation-btn"
                >
                  <Navigation className="w-5 h-5" />
                  Start Navigation
                </button>
              </div>
            </div>

            {/* Scheduled Time */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-2">Pickup Slot</h3>
              <p className="text-gray-600">
                📅 {pickup.pickup_scheduled_date} • {pickup.pickup_scheduled_time || 'Flexible'}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Start QC Button - only show when arrived */}
              {(pickup.status === 'ARRIVED' || pickup.status === 'PQ_IN_PROGRESS') && (
                <button
                  onClick={() => setActiveTab('imei')}
                  className="w-full py-4 bg-[#2b3380] text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-2"
                  data-testid="start-qc-btn"
                >
                  <ScanLine className="w-5 h-5" />
                  {pickup.status === 'PQ_IN_PROGRESS' ? 'Continue QC Assessment' : 'Start Device Verification'}
                </button>
              )}
              
              {/* Mark as Failed Button */}
              {pickup.status !== 'PQ_COMPLETED' && pickup.status !== 'PICKUP_FAILED' && (
                <button
                  onClick={() => setShowFailureModal(true)}
                  className="w-full py-3 border-2 border-red-300 text-red-600 rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-red-50"
                  data-testid="mark-failed-btn"
                >
                  <XCircle className="w-5 h-5" />
                  Mark Pickup as Failed
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* IMEI Tab */}
        {activeTab === 'imei' && (
          <div className="space-y-4">
            <IMEICapture 
              initialImei={pickup?.device_info?.imei}
              leadId={leadId}
              onSave={(data) => {
                setImeiSaved(true);
                setPickup(prev => ({
                  ...prev,
                  device_info: { ...prev.device_info, ...data }
                }));
              }}
            />
            
            <button
              onClick={() => setActiveTab('qc')}
              className="w-full py-4 bg-[#2b3380] text-white rounded-xl font-semibold flex items-center justify-center gap-2"
            >
              Next: QC Assessment →
            </button>
          </div>
        )}

        {/* QC Tab - Simple punch results, no analytics */}
        {activeTab === 'qc' && qcConfig && (
          <div className="space-y-6">
            {/* Progress indicator */}
            <div className="bg-gradient-to-r from-[#2b3380] to-[#4a5cc5] rounded-xl p-4 shadow-sm text-white">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm opacity-90">QC Progress</span>
                <span className="text-sm font-bold">
                  {Object.keys(qcResponses).length} / {qcConfig.categories.reduce((sum, cat) => sum + cat.parameters.length, 0)} checked
                </span>
              </div>
              <div className="w-full h-3 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white transition-all duration-300 rounded-full"
                  style={{ 
                    width: `${(Object.keys(qcResponses).length / qcConfig.categories.reduce((sum, cat) => sum + cat.parameters.length, 0)) * 100}%` 
                  }}
                />
              </div>
              <p className="text-xs mt-2 opacity-80">
                {Math.round((Object.keys(qcResponses).length / qcConfig.categories.reduce((sum, cat) => sum + cat.parameters.length, 0)) * 100)}% Complete
              </p>
            </div>

            {qcConfig.categories.map((category, catIndex) => {
              // Count completed params in this category
              const completedInCategory = category.parameters.filter(p => qcResponses[p.id]).length;
              const totalInCategory = category.parameters.length;
              const isComplete = completedInCategory === totalInCategory;
              
              // Category icons
              const categoryIcons = {
                'device_status': '📱',
                'battery': '🔋',
                'display': '🖥️',
                'touch': '👆',
                'network': '📶',
                'camera': '📷',
                'functional': '⚙️',
                'physical': '🔧',
                'packaging': '📦'
              };
              
              return (
                <div key={category.id} className="space-y-3">
                  {/* Category Header - More prominent */}
                  <div className={`flex items-center justify-between p-3 rounded-xl ${
                    isComplete ? 'bg-green-100 border-2 border-green-300' : 'bg-gray-100 border-2 border-gray-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{categoryIcons[category.id] || '📋'}</span>
                      <div>
                        <h3 className={`font-bold text-lg ${isComplete ? 'text-green-800' : 'text-gray-800'}`}>
                          {category.name}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {completedInCategory}/{totalInCategory} parameters
                        </p>
                      </div>
                    </div>
                    {isComplete && (
                      <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                        ✓ DONE
                      </span>
                    )}
                  </div>
                  
                  {/* Parameters inside category */}
                  <div className="pl-2 border-l-4 border-gray-200 space-y-2">
                    {category.parameters.map(param => (
                      <QCQuestion
                        key={param.id}
                        question={param}
                        value={qcResponses[param.id]}
                        onChange={(option) => handleQcChange(param.id, option)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            <button
              onClick={() => setActiveTab('photos')}
              className="w-full py-4 bg-[#2b3380] text-white rounded-xl font-semibold"
            >
              Next: Capture Photos →
            </button>
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3">Device Photos</h3>
              <p className="text-sm text-gray-500 mb-4">
                Capture front, back, and any damage on the device
              </p>
              <CameraCapture
                photos={photos}
                onCapture={handlePhotoCapture}
                onRemove={handlePhotoRemove}
              />
            </div>

            <button
              onClick={() => setActiveTab('signature')}
              disabled={photos.length === 0}
              className="w-full py-4 bg-[#2b3380] text-white rounded-xl font-semibold disabled:bg-gray-300"
            >
              Next: Get Signature →
            </button>
          </div>
        )}

        {/* Signature Tab */}
        {activeTab === 'signature' && (
          <div className="space-y-4">
            {submitSuccess ? (
              /* Success Screen */
              <div className="bg-white rounded-xl p-6 shadow-sm text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">PQ Submitted Successfully!</h3>
                <p className="text-gray-500 mb-4">Lead: {leadId}</p>
                
                {calculatedPrice && (
                  <div className="bg-gray-50 rounded-xl p-4 mb-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Quoted Price</p>
                        <p className="font-bold text-lg">₹{calculatedPrice.quoted?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Final Price</p>
                        <p className="font-bold text-lg text-green-600">₹{calculatedPrice.final?.toLocaleString()}</p>
                      </div>
                    </div>
                    {calculatedPrice.grade && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          Grade: {calculatedPrice.grade}
                        </span>
                        {calculatedPrice.difference !== 0 && (
                          <span className={`ml-2 text-sm ${calculatedPrice.difference > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {calculatedPrice.difference > 0 ? '-' : '+'}₹{Math.abs(calculatedPrice.difference).toLocaleString()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                <p className="text-sm text-gray-500">Redirecting to dashboard...</p>
              </div>
            ) : (
              /* Signature Capture */
              <>
                {/* Price Negotiation Section - Show difference to seller */}
                <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-4 shadow-sm text-white">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Price Discussion with Customer
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/20 rounded-lg p-3">
                      <p className="text-xs opacity-80">Quoted to Customer</p>
                      <p className="text-2xl font-bold">₹{pickup?.quoted_price?.toLocaleString()}</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3">
                      <p className="text-xs opacity-80">Based on QC</p>
                      <p className="text-2xl font-bold">₹{gradePreview?.final_price?.toLocaleString() || '--'}</p>
                    </div>
                  </div>
                  {gradePreview && gradePreview.final_price < pickup?.quoted_price && (
                    <div className="mt-3 p-3 bg-white/10 rounded-lg">
                      <p className="text-sm font-medium">
                        💰 Price Difference: <span className="text-yellow-200">-₹{(pickup?.quoted_price - gradePreview.final_price).toLocaleString()}</span>
                      </p>
                      <p className="text-xs mt-1 opacity-80">
                        Discuss the QC findings with customer and negotiate the final price
                      </p>
                    </div>
                  )}
                  {gradePreview && gradePreview.final_price >= pickup?.quoted_price && (
                    <div className="mt-3 p-3 bg-green-500/30 rounded-lg">
                      <p className="text-sm font-medium">
                        ✅ Device matches quoted condition
                        {gradePreview.packaging_topup > 0 && (
                          <span className="text-green-200"> (+₹{gradePreview.packaging_topup} for accessories)</span>
                        )}
                      </p>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <h3 className="font-semibold text-gray-800 mb-3">Customer Signature</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    After price agreement, ask customer to sign below to confirm device handover
                  </p>
                  
                  {signature ? (
                    <div className="space-y-3">
                      <div className="border rounded-xl p-2 bg-gray-50">
                        <img src={signature} alt="Signature" className="w-full" />
                      </div>
                      <button
                        onClick={() => setSignature(null)}
                        className="w-full py-2 border border-gray-300 rounded-lg text-gray-600"
                      >
                        Re-capture Signature
                      </button>
                    </div>
                  ) : (
                    <SignaturePad
                      onSave={(sig) => setSignature(sig)}
                      onClear={() => setSignature(null)}
                    />
                  )}
                </div>

                {/* Summary */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <h3 className="font-semibold text-gray-800 mb-3">Submission Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">QC Completed</span>
                      <span className={`font-medium ${qcProgress >= 50 ? 'text-green-600' : 'text-orange-600'}`}>
                        {qcProgress}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Photos</span>
                      <span className={`font-medium ${photos.length > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {photos.length} captured
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Signature</span>
                      <span className={`font-medium ${signature ? 'text-green-600' : 'text-red-600'}`}>
                        {signature ? 'Captured' : 'Pending'}
                      </span>
                    </div>
                    {isDemo && (
                      <div className="flex justify-between pt-2 border-t border-gray-100">
                        <span className="text-yellow-600 text-xs">⚠️ Demo Mode</span>
                        <span className="text-yellow-600 text-xs">Data not saved</span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Submit Button - Fixed at bottom */}
      {activeTab === 'signature' && !submitSuccess && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
          <button
            onClick={handleSubmitPQ}
            disabled={submitting || !signature || photos.length === 0}
            className="w-full py-4 bg-green-600 text-white rounded-xl font-semibold text-lg disabled:bg-gray-300 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Submit PQ & Complete Pickup
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Failure Modal */}
      <FailureModal
        isOpen={showFailureModal}
        onClose={() => setShowFailureModal(false)}
        onSubmit={handlePickupFailed}
        loading={statusUpdating}
      />
    </div>
  );
};

export default PickupDetail;
